import { AUTH_STORAGE_KEYS } from '../constants/auth.constants'
import type { AuthSessionModel, AuthUserModel } from '../models/auth.model'

/**
 * Persists the active auth session across reloads.
 *
 * Always backed by `localStorage` so the session survives a tab close. A
 * leftover key from a previous build that used `sessionStorage` is migrated on
 * first read.
 *
 * `session.expiresAt` IS NOT THE SESSION'S LIFETIME — it is the ACCESS token's,
 * and that is fifteen minutes. The durable credential is the HttpOnly
 * `inmeet_session` cookie, good for thirty days, which this file cannot see and
 * must not second-guess.
 *
 * `load()` used to delete the blob once `expiresAt` had passed. That was
 * written when the access token lasted 24 hours, so it read as an expiry check;
 * when the token dropped to fifteen minutes it quietly became "sign the user
 * out after fifteen minutes away from an open tab." Anyone who closed the tab
 * over lunch came back to a login screen while their thirty-day session sat
 * untouched on the server — the refresh that would have restored it was never
 * attempted, because this had already decided there was nothing to restore.
 *
 * An expired access token is now simply an expired access token: the blob is
 * returned, and the boot path renews it. The only thing that ends a session is
 * the server refusing to refresh it, which clears storage through
 * `announceReauthRequired`.
 */
type PersistedSession = {
  session: AuthSessionModel
  user: AuthUserModel
  /**
   * Per-org chat/presence identity (`contactPresenceId`). Persisted with the
   * session so a reload can start chat + presence subscriptions IMMEDIATELY
   * instead of waiting on `GET /contacts/self`. Stored inside the session blob
   * (not a separate key) so it is inseparable from the user it belongs to — a
   * different user's login overwrites both at once.
   */
  presenceId?: string | null
}

const KEY = AUTH_STORAGE_KEYS.SESSION

const clearAll = () => {
  localStorage.removeItem(KEY)
  // Legacy tab-only storage path from earlier builds.
  sessionStorage.removeItem(KEY)
}

export const sessionStorageRepo = {
  save(payload: PersistedSession, rememberMe: boolean) {
    clearAll()
    localStorage.setItem(KEY, JSON.stringify(payload))
    if (rememberMe) {
      localStorage.setItem(AUTH_STORAGE_KEYS.REMEMBER_ME, '1')
    } else {
      localStorage.removeItem(AUTH_STORAGE_KEYS.REMEMBER_ME)
    }
  },

  /**
   * Merge a resolved `presenceId` into the stored session, leaving the rest
   * untouched. No-op when nothing is stored (signed out mid-resolve) or when the
   * stored session belongs to a different user — a stale id from another account
   * would key chat and presence to the wrong identity.
   */
  savePresenceId(userId: string, presenceId: string | null) {
    const raw = localStorage.getItem(KEY)
    if (!raw) return
    try {
      const parsed = JSON.parse(raw) as PersistedSession
      if (parsed?.user?.id !== userId) return
      localStorage.setItem(KEY, JSON.stringify({ ...parsed, presenceId }))
    } catch {
      /* corrupt blob — the next successful login rewrites it */
    }
  },

  /**
   * Merge fields into the stored user, leaving the session and presenceId alone.
   *
   * Without this, anything learned about the user AFTER login lived only in
   * memory: `setAvatarUrl` updated the header the moment a picture was
   * uploaded, then `hydrate()` restored the login-time blob on the next reload
   * and the picture vanished. Same user-id guard as `savePresenceId` — a patch
   * from a signed-out or switched account must not land on someone else's row.
   */
  patchUser(userId: string, patch: Partial<AuthUserModel>) {
    const raw = localStorage.getItem(KEY)
    if (!raw) return
    try {
      const parsed = JSON.parse(raw) as PersistedSession
      if (parsed?.user?.id !== userId) return
      localStorage.setItem(
        KEY,
        JSON.stringify({ ...parsed, user: { ...parsed.user, ...patch } }),
      )
    } catch {
      /* corrupt blob — the next successful login rewrites it */
    }
  },

  load(): PersistedSession | null {
    // Prefer the canonical localStorage slot. Fall back to a legacy
    // sessionStorage value (only matters for users mid-migration) and
    // promote it so the next read finds it in the canonical place.
    const fromLocal = localStorage.getItem(KEY)
    const fromSession = fromLocal === null ? sessionStorage.getItem(KEY) : null
    const raw = fromLocal ?? fromSession
    if (!raw) return null
    try {
      const parsed = JSON.parse(raw) as PersistedSession
      if (!parsed?.session || !parsed?.user) return null
      // Deliberately NO expiry check — see the note above. A stale access token
      // is renewed on boot; a session that is genuinely over is ended by the
      // refresh call failing, which is the only party that actually knows.
      if (fromSession !== null) {
        // Migrate the legacy session-scoped value into localStorage so it
        // survives the next tab close.
        localStorage.setItem(KEY, raw)
        sessionStorage.removeItem(KEY)
      }
      return parsed
    } catch {
      return null
    }
  },

  clear() {
    clearAll()
    localStorage.removeItem(AUTH_STORAGE_KEYS.REMEMBER_ME)
  },
}
